import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploader } from 'react-drag-drop-files';
import Select from 'react-select';
import {
	FaExclamationTriangle,
	FaCheckCircle,
	FaTimesCircle,
	FaRegCopy,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';
import {
	Badge,
	Card,
	Col,
	Container,
	Modal,
	ProgressBar,
	Row,
	Spinner,
} from 'react-bootstrap';
import toaster from 'src/utils/toaster';
import Breadcrumb from 'src/components/Breadcrumb/Breadcrumb';
import Btn from 'src/components/Btn/Btn';
import TableContainer from 'src/components/genericTable/TableContainer';
import ToolTip from 'src/components/Tooltip/Tooltip';
import { SegmentedControl } from 'src/components/SegmentedControl/SegmentedControl';
import {
	FIELDS_MAPPER,
	MAX_IMPORT_ROWS,
	parseImportExcel,
	invalidFields,
	type ImportedVehicleRow,
	type ImportField,
} from './parseImportExcel';
import { downloadImportTemplate } from './importTemplate';
import {
	downloadFailedRows,
	shouldExportRow,
	type FailedRowExport,
} from './exportFailedRows';
import type { MotornetVehicleImportValidationMatch } from '../types';
import {
	matchHasColorNotFound,
	missingRequiredInteriorColor,
} from './importStatus';
import type { ImportRowStatus, ImportStatusCode } from './importStatus';
import { useUnloadPrompt } from '../common/useUnloadPrompt';
import { useImportValidation } from './useImportValidation';
import { useExcelImport, type ExcelImportItem } from './useExcelImport';
import { rowToExcelRequest } from './rowToExcelRequest';
import {
	useImportOptionalCatalogs,
	catalogKeyOf,
	type VehicleCatalogKey,
} from './useImportOptionalCatalogs';
import { mergeColorOptionalCodes } from '../common/colorOptionals';
import { CgMoreO } from 'react-icons/cg';

type StatusFilter = 'all' | 'resolve' | 'fix' | 'ready';

const SEGMENT_TO_STATUS: Record<
	Exclude<StatusFilter, 'all'>,
	ImportStatusCode
> = {
	resolve: 'ambiguous',
	fix: 'invalid',
	ready: 'valid',
};

const fileTypes = ['xlsx', 'xls'];

const COLUMN_LABELS: Partial<Record<ImportField, string>> = {
	dealer: 'COD. DEALER',
	nr_ordine: 'NR. ORDINE',
	codice_costruttore: 'COD. COSTRUTTORE',
	codice_colore_esterno: 'COD. COLORE ESTERNO',
	codice_colore_interno: 'COD. COLORE INTERNO',
	optional_list: 'COD. OPTIONALS',
};

const ESSENTIAL_FIELDS: ImportField[] = [
	'codice_costruttore',
	'nr_ordine',
	'codice_colore_esterno',
	'codice_colore_interno',
	'optional_list',
];

const DETAIL_FIELD_ENTRIES = (
	Object.entries(FIELDS_MAPPER) as [string, ImportField][]
).filter(([, field]) => !ESSENTIAL_FIELDS.includes(field));

const HIDDEN_FIELDS = DETAIL_FIELD_ENTRIES.map(([, field]) => field);

const matchKey = (match: MotornetVehicleImportValidationMatch) =>
	`${match.vehicleKind}:${match.eurotaxCode}`;

const matchLabel = (match: MotornetVehicleImportValidationMatch) =>
	[match.marca, match.modello, match.allestimento, match.manufactureCode]
		.filter(Boolean)
		.join(' · ');

const STATUS_ORDER: Record<ImportRowStatus, number> = {
	ambiguous: 0,
	invalid: 1,
	checking: 2,
	valid: 3,
};

const STATUS_ICON: Record<
	Exclude<ImportRowStatus, 'checking'>,
	{ Icon: IconType; className: string }
> = {
	valid: { Icon: FaCheckCircle, className: 'text-success' },
	invalid: { Icon: FaTimesCircle, className: 'text-danger' },
	ambiguous: { Icon: FaExclamationTriangle, className: 'text-warning' },
};

const ImportStatusIcon = ({
	status,
	id,
	className,
}: {
	status: ImportRowStatus;
	id?: string;
	className?: string;
}) => {
	if (status === 'checking') {
		return (
			<Spinner animation="border" size="sm" id={id} className={className} />
		);
	}
	const { Icon, className: colorClass } = STATUS_ICON[status];
	return (
		<Icon
			id={id}
			size={18}
			className={[colorClass, className].filter(Boolean).join(' ')}
		/>
	);
};

const MotornetImportPage = () => {
	const { t } = useTranslation();

	const [rows, setRows] = useState<ImportedVehicleRow[]>([]);
	const [fileName, setFileName] = useState<string | null>(null);
	const [parsing, setParsing] = useState(false);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
	const [saveModalOpen, setSaveModalOpen] = useState(false);
	const [validationDismissed, setValidationDismissed] = useState(false);
	const [resolvedByRow, setResolvedByRow] = useState<Record<number, string>>(
		{}
	);

	const {
		statusForRow,
		matchesForRow,
		errorForRow,
		isChecking,
		isRetrying,
		progress: validationProgress,
		failedRows: validationFailedRows,
		retryFailed: retryValidation,
	} = useImportValidation(rows);
	const {
		outcomes,
		run: runImport,
		reset: resetOutcomes,
		isRunning,
	} = useExcelImport();

	const autoFilterAppliedRef = useRef(false);

	const activeMatchForRow = useCallback(
		(original: ImportedVehicleRow & { _rowIndex: number }) => {
			const matches = matchesForRow(original);
			if (matches.length <= 1) return matches[0];
			return (
				matches.find(
					(match) => matchKey(match) === resolvedByRow[original._rowIndex]
				) ?? matches[0]
			);
		},
		[matchesForRow, resolvedByRow]
	);

	const warningsForRow = useCallback(
		(original: ImportedVehicleRow & { _rowIndex: number }) => {
			const active = activeMatchForRow(original);

			const optionMessages = new Map<string, string>();
			let internalColor: string | undefined;
			let externalColor: string | undefined;

			(active?.warnings ?? []).forEach((warning) => {
				switch (warning.code) {
					case 'OPTION_CODE_NOT_FOUND':
						if (warning.optionCode)
							optionMessages.set(warning.optionCode, warning.message);
						break;
					case 'INTERNAL_COLOR_CODE_NOT_FOUND':
						internalColor = warning.message;
						break;
					case 'EXTERNAL_COLOR_CODE_NOT_FOUND':
						externalColor = warning.message;
						break;
				}
			});

			return { optionMessages, internalColor, externalColor };
		},
		[activeMatchForRow]
	);

	const invalidReason = useCallback(
		(
			row: ImportedVehicleRow & {
				_rowIndex: number;
				_catalogError?: string;
				_interiorColorMissing?: boolean;
			},
			status: ImportRowStatus
		): string | undefined => {
			if (status !== 'invalid') return undefined;
			if (row._interiorColorMissing) {
				return t(
					'motornet_import_interior_color_required',
					'COD. COLORE INTERNO obbligatorio: il listino di questo veicolo ha colori interni'
				);
			}
			if (row._catalogError) {
				return t(
					'motornet_import_catalog_failed',
					'Listino non caricato: {{error}}',
					{ error: row._catalogError }
				);
			}
			const failed = invalidFields(row);
			if (failed.length) {
				const labels = failed
					.map((field) => COLUMN_LABELS[field] ?? field)
					.join(', ');
				return t(
					'motornet_import_invalid_fields',
					'Campi obbligatori mancanti o non validi: {{fields}}',
					{ fields: labels }
				);
			}
			const { internalColor, externalColor } = warningsForRow(row);
			if (externalColor || internalColor) {
				return [externalColor, internalColor].filter(Boolean).join(' · ');
			}
			return t(
				'motornet_import_no_match',
				'Nessun veicolo corrispondente trovato'
			);
		},
		[t, warningsForRow]
	);

	const reasonForRow = useCallback(
		(
			row: ImportedVehicleRow & {
				_rowIndex: number;
				_catalogError?: string;
				_interiorColorMissing?: boolean;
			},
			status: ImportRowStatus
		): string | undefined =>
			errorForRow(row) ??
			invalidReason(row, status) ??
			(status === 'ambiguous'
				? t(
					'motornet_import_ambiguous',
					'Codice costruttore ambiguo: seleziona una versione specifica.'
				)
				: undefined),
		[t, errorForRow, invalidReason]
	);

	/**
	 * Stato "prima del listino": e' quello da cui ricaviamo i veicoli da
	 * caricare. Tenerlo separato da `enrichedRows` spezza il ciclo — riga valida
	 * -> chiave da caricare -> listino in attesa -> riga non piu' valida -> la
	 * chiave sparisce e il caricamento non parte mai.
	 */
	const baseRows = useMemo(
		() =>
			rows.map((row, index) => {
				const status = statusForRow(row);
				const enriched = { ...row, _rowIndex: index };
				let status_code: ImportRowStatus = status;
				if (status === 'ambiguous' && resolvedByRow[index]) {
					const match = activeMatchForRow(enriched);
					status_code =
						match && !matchHasColorNotFound(match) ? 'valid' : 'invalid';
				}
				return {
					...enriched,
					_sortRank: STATUS_ORDER[status],
					status_code,
				};
			}),
		[rows, statusForRow, resolvedByRow, activeMatchForRow]
	);

	/** Solo le righe gia' valide: le ambigue non risolte non vanno interrogate. */
	const catalogKeys = useMemo<VehicleCatalogKey[]>(() => {
		const byId = new Map<string, VehicleCatalogKey>();
		baseRows
			.filter((row) => row.status_code === 'valid')
			.forEach((row) => {
				const match = activeMatchForRow(row);
				if (!match?.eurotaxCode) return;
				const key: VehicleCatalogKey = {
					eurotaxCode: match.eurotaxCode,
					vehicleKind: match.vehicleKind,
				};
				byId.set(catalogKeyOf(key), key);
			});
		return [...byId.values()].sort((a, b) =>
			catalogKeyOf(a).localeCompare(catalogKeyOf(b))
		);
	}, [baseRows, activeMatchForRow]);

	const {
		stateFor: catalogStateFor,
		progress: catalogProgress,
		isInitialLoading: isLoadingCatalogs,
		failedCatalogs,
		retryFailed: retryCatalogs,
		reset: resetCatalogs,
	} = useImportOptionalCatalogs(catalogKeys);

	const enrichedRows = useMemo(
		() =>
			baseRows.map((row) => {
				if (row.status_code !== 'valid') {
					return {
						...row,
						_optionalCodes: row.optional_list,
						_colorOptionalCodes: [] as string[],
					};
				}
				const match = activeMatchForRow(row);
				const catalog = catalogStateFor(
					match?.eurotaxCode
						? { eurotaxCode: match.eurotaxCode, vehicleKind: match.vehicleKind }
						: null
				);
				/**
				 * Una riga arriva a `valid` solo se il backend ha gia' risolto i suoi
				 * codici colore: un listino che non si carica e' un problema di rete,
				 * non un dato mancante. Importarla comunque significherebbe salvarla
				 * al prezzo sbagliato senza dirlo a nessuno, quindi si blocca.
				 */
				if (catalog.pending) {
					return {
						...row,
						status_code: 'checking' as ImportRowStatus,
						_optionalCodes: row.optional_list,
						_colorOptionalCodes: [] as string[],
					};
				}
				if (catalog.error || !catalog.index) {
					return {
						...row,
						status_code: 'invalid' as ImportRowStatus,
						_catalogError: catalog.error ?? undefined,
						_optionalCodes: row.optional_list,
						_colorOptionalCodes: [] as string[],
					};
				}
				if (missingRequiredInteriorColor(row, catalog.index)) {
					return {
						...row,
						status_code: 'invalid' as ImportRowStatus,
						_sortRank: STATUS_ORDER.invalid,
						_interiorColorMissing: true,
						_optionalCodes: row.optional_list,
						_colorOptionalCodes: [] as string[],
					};
				}
				const { codes, fromColor } = mergeColorOptionalCodes(
					row.optional_list,
					catalog.index,
					{
						exterior: row.codice_colore_esterno,
						interior: row.codice_colore_interno,
					}
				);
				return { ...row, _optionalCodes: codes, _colorOptionalCodes: fromColor };
			}),
		[baseRows, activeMatchForRow, catalogStateFor]
	);

	const counts = useMemo(() => {
		const acc: Record<ImportStatusCode, number> = {
			valid: 0,
			invalid: 0,
			ambiguous: 0,
		};
		enrichedRows.forEach((row) => {
			if (row.status_code !== 'checking') acc[row.status_code] += 1;
		});
		return {
			resolve: acc.ambiguous,
			fix: acc.invalid,
			ready: acc.valid,
		};
	}, [enrichedRows]);

	const importableItems = useMemo<ExcelImportItem[]>(
		() =>
			enrichedRows
				.filter((row) => row.status_code === 'valid')
				.map((row) => {
					const match = activeMatchForRow(row);
					return match
						? {
							rowId: row.rowId,
							body: rowToExcelRequest(row, match, row._optionalCodes),
						}
						: null;
				})
				.filter((item): item is ExcelImportItem => item !== null),
		[enrichedRows, activeMatchForRow]
	);

	const progress = useMemo(() => {
		let success = 0;
		let error = 0;
		importableItems.forEach((item) => {
			const status = outcomes[item.rowId]?.status;
			if (status === 'success') success += 1;
			else if (status === 'error') error += 1;
		});
		return {
			total: importableItems.length,
			success,
			error,
			done: success + error,
		};
	}, [importableItems, outcomes]);

	/** Righe in attesa del listino: bloccano il salvataggio. */
	const catalogsPending = useMemo(
		() => enrichedRows.filter((row) => row.status_code === 'checking').length,
		[enrichedRows]
	);

	/** In ordine di file; se l'import e' fallito vale l'errore dell'API. */
	const failedRowsToExport = useMemo<FailedRowExport[]>(
		() =>
			enrichedRows
				.filter((row) => shouldExportRow(row.status_code, outcomes[row.rowId]))
				.map((row) => {
					const outcome = outcomes[row.rowId];
					const importError =
						outcome?.status === 'error' ? outcome.error : undefined;
					return {
						row,
						reason: importError ?? reasonForRow(row, row.status_code) ?? '',
					};
				}),
		[enrichedRows, outcomes, reasonForRow]
	);

	const isValidating = isChecking || isRetrying || isLoadingCatalogs;

	const hasImported = Object.keys(outcomes).length > 0;

	useUnloadPrompt(isRunning);

	useEffect(() => {
		if (autoFilterAppliedRef.current || isChecking) return;
		autoFilterAppliedRef.current = true;
		if (counts.resolve > 0) setStatusFilter('resolve');
	}, [isChecking, counts.resolve]);

	const filteredRows = useMemo(() => {
		const filtered =
			statusFilter === 'all'
				? enrichedRows
				: enrichedRows.filter(
					(row) => row.status_code === SEGMENT_TO_STATUS[statusFilter]
				);
		return [...filtered].sort((a, b) => a._sortRank - b._sortRank);
	}, [enrichedRows, statusFilter]);

	const columns = useMemo(() => {
		const entries = Object.entries(FIELDS_MAPPER) as [string, ImportField][];
		const rest = entries.filter(([, field]) => field !== 'codice_costruttore');
		return [
			{
				Header: t('status', 'STATO'),
				accessor: 'status_code',
				Cell: ({
					value,
					row,
				}: {
					value: ImportRowStatus;
					row: { original: ImportedVehicleRow & { _rowIndex: number } };
				}) => {
					const reason = reasonForRow(row.original, value);
					if (!reason) return <ImportStatusIcon status={value} />;
					const anchorId = `validation-error-${row.original._rowIndex}`;
					return (
						<>
							<ImportStatusIcon
								status={value}
								id={anchorId}
								className="role-button"
							/>
							<ToolTip anchorId={anchorId} content={reason} fixed={false} />
						</>
					);
				},
			},
			{
				id: 'import_status',
				Header: t('motornet_import_status_col', 'IMPORT'),
				disableSortBy: true,
				Cell: ({
					row,
				}: {
					row: { original: ImportedVehicleRow & { _rowIndex: number } };
				}) => {
					const outcome = outcomes[row.original.rowId];
					if (!outcome) return <span className="text-muted">—</span>;
					switch (outcome.status) {
						case 'queued':
							return <span className="text-muted">…</span>;
						case 'importing':
							return <Spinner animation="border" size="sm" />;
						case 'success':
							return <FaCheckCircle className="text-success" size={18} />;
						case 'error': {
							const anchorId = `import-status-${row.original._rowIndex}`;
							return (
								<>
									<span
										id={anchorId}
										className="text-danger d-inline-flex role-button"
									>
										<FaTimesCircle size={18} />
									</span>
									<ToolTip
										anchorId={anchorId}
										content={outcome.error && outcome.error.length > 100
											? `${outcome.error.slice(0, 100)}...`
											: outcome.error}
										fixed={false}
									/>
								</>
							);
						}
						default:
							return null;
					}
				},
			},
			{
				Header: COLUMN_LABELS.codice_costruttore,
				accessor: 'codice_costruttore',
				Cell: ({
					value,
					row,
				}: {
					value: string;
					row: { original: ImportedVehicleRow & { _rowIndex: number } };
				}) => {
					const matches = matchesForRow(row.original);
					if (matches.length <= 1) return value;

					const rowIndex = row.original._rowIndex;
					const options = matches.map((match) => ({
						value: matchKey(match),
						label: matchLabel(match),
					}));
					const selected =
						options.find(
							(option) => option.value === resolvedByRow[rowIndex]
						) ?? null;

					return (
						<Select
							options={options}
							value={selected}
							isClearable
							placeholder={`${value} — ${t(
								'motornet_import_pick_version',
								'seleziona versione…'
							)}`}
							menuPortalTarget={document.body}
							styles={{
								container: (base) => ({ ...base, minWidth: 300 }),
								menuPortal: (base) => ({ ...base, zIndex: 1090 }),
							}}
							onChange={(option) =>
								setResolvedByRow((prev) => {
									const next = { ...prev };
									if (option) next[rowIndex] = option.value;
									else delete next[rowIndex];
									return next;
								})
							}
						/>
					);
				},
			},
			{
				id: 'motornet_manufacture_code',
				Header: t('motornet_import_mn_code', 'COD. COSTRUTTORE RISOLTO'),
				disableSortBy: true,
				Cell: ({
					row,
				}: {
					row: { original: ImportedVehicleRow & { _rowIndex: number } };
				}) => {
					const unresolved =
						matchesForRow(row.original).length > 1 &&
						!resolvedByRow[row.original._rowIndex];
					const code = unresolved
						? undefined
						: activeMatchForRow(row.original)?.manufactureCode;
					return code ?? <span className="text-muted">—</span>;
				},
			},
			...rest.map(([header, field]) => {
				const label = COLUMN_LABELS[field] ?? header;
				if (field === 'optional_list') {
					return {
						Header: label,
						accessor: field,
						Cell: ({
							value,
							row,
						}: {
							value: string[];
							row: {
								original: ImportedVehicleRow & {
									_rowIndex: number;
									_optionalCodes?: string[];
									_colorOptionalCodes?: string[];
								};
							};
						}) => {
							const { optionMessages } = warningsForRow(row.original);
							const codes = row.original._optionalCodes ?? value;
							const fromColor = row.original._colorOptionalCodes ?? [];
							return codes.length ? (
								<div className="d-flex gap-1 flex-wrap">
									{codes.map((code) => {
										if (fromColor.includes(code)) {
											const colorAnchorId = `import-optcolor-${row.original._rowIndex}-${code}`;
											return (
												<Fragment key={code}>
													<Badge id={colorAnchorId} bg="info" text="dark">
														{code}
													</Badge>
													<ToolTip
														anchorId={colorAnchorId}
														content={t(
															'motornet_import_color_optional',
															'Colore incluso negli optional'
														)}
														fixed={false}
													/>
												</Fragment>
											);
										}
										const message = optionMessages.get(code);
										if (!message)
											return (
												<Badge key={code} bg="light" text="dark">
													{code}
												</Badge>
											);
										const anchorId = `import-opt-${row.original._rowIndex}-${code}`;
										return (
											<Fragment key={code}>
												<Badge id={anchorId} bg="warning" text="dark">
													{code}
												</Badge>
												<ToolTip
													anchorId={anchorId}
													content={message}
													fixed={false}
												/>
											</Fragment>
										);
									})}
								</div>
							) : null;
						},
					};
				}

				if (
					field === 'codice_colore_esterno' ||
					field === 'codice_colore_interno'
				) {
					return {
						Header: label,
						accessor: field,
						Cell: ({
							value,
							row,
						}: {
							value: string;
							row: { original: ImportedVehicleRow & { _rowIndex: number } };
						}) => {
							if (!value) return null;
							const { internalColor, externalColor } = warningsForRow(
								row.original
							);
							const message =
								field === 'codice_colore_interno'
									? internalColor
									: externalColor;
							if (!message) return value;
							const anchorId = `import-${field}-${row.original._rowIndex}`;
							return (
								<>
									<span
										id={anchorId}
										className="text-danger d-inline-flex align-items-center gap-1"
									>
										<FaExclamationTriangle />
										{value}
									</span>
									<ToolTip
										anchorId={anchorId}
										content={message}
										fixed={false}
									/>
								</>
							);
						},
					};
				}

				return { Header: label, accessor: field };
			}),
			{
				id: 'altro',
				Header: t('motornet_import_other', 'Altro'),
				disableSortBy: true,
				Cell: ({
					row,
				}: {
					row: { original: ImportedVehicleRow & { _rowIndex: number } };
				}) => {
					const original = row.original;
					const filled = DETAIL_FIELD_ENTRIES.filter(
						([, field]) => original[field]
					);
					if (!filled.length) {
						return <span className="text-muted">—</span>;
					}

					const anchorId = `import-altro-${original._rowIndex}`;
					return (
						<>
							<span
								id={anchorId}
								className="role-button text-primary d-inline-flex"
							>
								<CgMoreO
									className="text-bold"
									style={{ fontWeight: 'bolder' }}
									size={24}
								/>
							</span>
							<ToolTip
								anchorId={anchorId}
								fixed={false}
								place="left"
								clickable
								className="wide"
							>
								<div className="text-start">
									{filled.map(([header, field]) => (
										<div key={field}>
											<span className="text-bold">
												{COLUMN_LABELS[field] ?? header}:
											</span>{' '}
											{original[field]}
										</div>
									))}
								</div>
							</ToolTip>
						</>
					);
				},
			},
		];
	}, [
		t,
		matchesForRow,
		activeMatchForRow,
		resolvedByRow,
		warningsForRow,
		outcomes,
		reasonForRow,
	]);

	const segments = useMemo(
		() => [
			{
				value: 'all',
				label: `${t('motornet_import_seg_all', 'Tutti')} (${enrichedRows.length
					})`,
			},
			{
				value: 'resolve',
				label: `${t('motornet_import_seg_resolve', 'Da risolvere')} (${counts.resolve
					})`,
			},
			{
				value: 'fix',
				label: `${t('motornet_import_seg_fix', 'Da correggere')} (${counts.fix
					})`,
			},
			{
				value: 'ready',
				label: `${t('motornet_import_seg_ready', 'Pronti')} (${counts.ready})`,
			},
		],
		[t, enrichedRows.length, counts]
	);

	const handleFile = async (file: File) => {
		setParsing(true);
		try {
			const parsed = await parseImportExcel(file);
			autoFilterAppliedRef.current = false;
			setRows(parsed);
			setFileName(file.name);
			setStatusFilter('all');
			setResolvedByRow({});
			setValidationDismissed(false);
			resetOutcomes();
			resetCatalogs();
		} catch (error) {
			toaster.error(
				error instanceof Error
					? error.message
					: t('motornet_import_parse_error', 'Impossibile leggere il file')
			);
		} finally {
			setParsing(false);
		}
	};

	const onTypeError = () => {
		toaster.error(t('unsupported_file'));
	};

	const handleDownloadTemplate = () => {
		downloadImportTemplate();
	};

	const handleReset = () => {
		autoFilterAppliedRef.current = false;
		setRows([]);
		setFileName(null);
		setStatusFilter('all');
		setResolvedByRow({});
		setValidationDismissed(false);
		resetOutcomes();
		resetCatalogs();
	};

	const handleDownloadFailedRows = () => {
		if (fileName) downloadFailedRows(failedRowsToExport, fileName);
	};

	const handleSave = () => {
		setSaveModalOpen(true);
	};

	const handleStartImport = () => {
		void runImport(importableItems);
	};

	const handleRetryFailed = () => {
		const failed = importableItems.filter(
			(item) => outcomes[item.rowId]?.status === 'error'
		);
		if (!failed.length) return;
		setSaveModalOpen(true);
		void runImport(failed);
	};

	const handleRetryValidation = () => {
		void retryValidation();
		void retryCatalogs();
	};

	const handleCopyValidationErrors = async () => {
		const failed = [
			...validationFailedRows.map(({ rowId, error }) => {
				const row = rows.find((r) => r.rowId === rowId);
				return { ...row, validation_error: error };
			}),
			...failedCatalogs.map(({ key, error }) => ({
				vehicle: key,
				catalog_error: error,
			})),
		];
		try {
			await navigator.clipboard.writeText(JSON.stringify(failed, null, 2));
			toaster.success(
				t('motornet_import_errors_copied', 'Righe con errore copiate')
			);
		} catch {
			toaster.error(t('motornet_import_copy_failed', 'Copia non riuscita'));
		}
	};

	const handleCopyErrors = async () => {
		const failed = importableItems
			.filter((item) => outcomes[item.rowId]?.status === 'error')
			.map((item) => {
				const row = rows.find((r) => r.rowId === item.rowId);
				return { ...row, api_error: outcomes[item.rowId]?.error };
			});
		try {
			await navigator.clipboard.writeText(JSON.stringify(failed, null, 2));
			toaster.success(
				t('motornet_import_errors_copied', 'Righe con errore copiate')
			);
		} catch {
			toaster.error(t('motornet_import_copy_failed', 'Copia non riuscita'));
		}
	};

	return (
		<Container fluid>
			<Breadcrumb
				item1={{
					name: t('vehicles_list'),
					link: '/available-vehicles',
				}}
				activeItem={{ name: t('import', 'Import') }}
			/>
			<div className="display-6 my-4">
				{t('motornet_import_title', 'Import veicoli da Excel')}
			</div>

			<Card className="mt-2">
				<Card.Body>
					{fileName === null ? (
						<>
							<FileUploader
								classes="dropzone text-center"
								fileOrFiles={null}
								types={fileTypes}
								handleChange={handleFile}
								onTypeError={onTypeError}
								name="file"
							>
								<div className="fs-4">
									<div>{t('drag_file_here_or_click_to_upload')}</div>
									<div className="fs-6 text-muted">
										{t('file_type')}: {fileTypes.join(', ')} ·{' '}
										{t(
											'motornet_import_max_rows',
											`max ${MAX_IMPORT_ROWS} righe`
										)}
									</div>
								</div>
							</FileUploader>
							<div className="d-flex justify-content-center mt-3">
								<Btn size="sm" onClick={handleDownloadTemplate}>
									{t(
										'motornet_import_download_template',
										'Scarica template Excel'
									)}
								</Btn>
							</div>
						</>
					) : (
						<>
							<Row className="align-items-center">
								<Col className="d-flex align-items-center gap-3">
									<strong>{fileName}</strong>
									<span className="text-muted">
										{t('motornet_import_rows_count', '{{count}} righe', {
											count: rows.length,
										})}
									</span>
								</Col>
								<Col xs="auto" className="d-flex gap-2">
									<Btn size="sm" onClick={handleReset} disabled={isRunning}>
										{t('motornet_import_reset', 'Carica un altro file')}
									</Btn>
									{!isValidating && failedRowsToExport.length > 0 && (
										<Btn
											size="sm"
											onClick={handleDownloadFailedRows}
											disabled={isRunning}
										>
											{t(
												'motornet_import_download_failed',
												'Scarica righe da correggere'
											)}{' '}
											({failedRowsToExport.length})
										</Btn>
									)}
									{!hasImported && (
										<Btn
											size="sm"
											onClick={handleSave}
											disabled={
												isChecking ||
												isRetrying ||
												catalogsPending > 0 ||
												importableItems.length === 0
											}
										>
											{t('motornet_import_save', 'Salva')}
										</Btn>
									)}
									{progress.error > 0 && (
										<Btn size="sm" onClick={handleRetryFailed}>
											{t('motornet_import_retry_failed', 'Riprova falliti')} (
											{progress.error})
										</Btn>
									)}
									{progress.error > 0 && (
										<Btn
											size="sm"
											onClick={handleCopyErrors}
											title={t(
												'motornet_import_copy_errors',
												'Copia righe con errore'
											)}
										>
											<FaRegCopy />
										</Btn>
									)}
								</Col>
							</Row>

							<div style={{ zoom: 0.85 }}>
								<div className="mt-4">
									<SegmentedControl
										data={segments}
										value={statusFilter}
										onChange={(value) => {
											autoFilterAppliedRef.current = true;
											setStatusFilter(value as StatusFilter);
										}}
										fullWidth
										size="md"
									/>
								</div>
								<TableContainer
									columns={columns}
									data={filteredRows}
									loading={parsing}
									error={null}
									initPageSize={20}
									initialState={{
										pageSize: 20,
										hiddenColumns: HIDDEN_FIELDS,
									}}
								/>
							</div>
						</>
					)}
				</Card.Body>
			</Card>

			{(() => {
				const validating = isValidating;
				const hasFailures =
					validationFailedRows.length > 0 || failedCatalogs.length > 0;
				const showValidationModal =
					validating || (hasFailures && !validationDismissed);
				const phaseProgress = isLoadingCatalogs
					? catalogProgress
					: validationProgress;
				const phaseLabel = isLoadingCatalogs
					? t('motornet_import_loading_catalogs', 'Caricamento listini in corso.')
					: t('motornet_import_validating_desc', 'Validazione righe in corso.');
				return (
					<Modal
						show={showValidationModal}
						onHide={() => !validating && setValidationDismissed(true)}
						centered
						backdrop="static"
						keyboard={false}
					>
						<Modal.Header closeButton={!validating}>
							<Modal.Title>
								{validating
									? t('motornet_import_validating', 'Validazione in corso…')
									: t(
										'motornet_import_validation_failed_title',
										'Validazione non riuscita'
									)}
							</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							{validating ? (
								<>
									<p className="mb-3">{phaseLabel}</p>
									<ProgressBar
										className="mb-2"
										now={phaseProgress.done}
										max={phaseProgress.total || 1}
										variant={phaseProgress.error > 0 ? 'warning' : 'primary'}
										animated={phaseProgress.done < phaseProgress.total}
									/>
									<div className="text-muted small d-flex align-items-center gap-2">
										<Spinner animation="border" size="sm" />
										{phaseProgress.done}/{phaseProgress.total}
										{phaseProgress.error > 0 &&
											` · ${phaseProgress.error} ${t(
												'motornet_import_failed',
												'falliti'
											)}`}
									</div>
								</>
							) : (
								<>
									<p className="mb-3">
										{t(
											'motornet_import_validation_failed_desc',
											'{{count}} righe non sono state validate.',
											{
												count:
													validationFailedRows.length + failedCatalogs.length,
											}
										)}
									</p>
									<div
										className="small"
										style={{ maxHeight: 200, overflowY: 'auto' }}
									>
										{validationFailedRows.map(({ rowId, error }) => {
											const row = rows.find((r) => r.rowId === rowId);
											return (
												<div
													key={rowId}
													className="d-flex align-items-start gap-2 border-bottom py-1"
												>
													<Badge
														bg="light"
														text="dark"
														className="flex-shrink-0 text-nowrap"
													>
														{row?.codice_costruttore ?? rowId}
													</Badge>
													<span className="text-danger">{error}</span>
												</div>
											);
										})}
										{failedCatalogs.map(({ key, error }) => (
											<div
												key={key}
												className="d-flex align-items-start gap-2 border-bottom py-1"
											>
												<Badge
													bg="light"
													text="dark"
													className="flex-shrink-0 text-nowrap"
												>
													{key}
												</Badge>
												<span className="text-danger">{error}</span>
											</div>
										))}
									</div>
								</>
							)}
						</Modal.Body>
						<Modal.Footer>
							{validating ? (
								<Btn size="sm" disabled>
									<Spinner animation="border" size="sm" className="me-2" />
									{t('motornet_import_validating', 'Validazione in corso…')}
								</Btn>
							) : (
								<>
									<Btn size="sm" onClick={handleRetryValidation}>
										{t('motornet_import_retry_failed', 'Riprova falliti')} (
										{validationFailedRows.length + failedCatalogs.length})
									</Btn>
									<Btn
										size="sm"
										onClick={handleCopyValidationErrors}
										title={t(
											'motornet_import_copy_errors',
											'Copia righe con errore'
										)}
									>
										<FaRegCopy />
									</Btn>
									<Btn size="sm" onClick={() => setValidationDismissed(true)}>
										{t('close', 'Chiudi')}
									</Btn>
								</>
							)}
						</Modal.Footer>
					</Modal>
				);
			})()}

			<Modal
				show={saveModalOpen}
				onHide={() => !isRunning && setSaveModalOpen(false)}
				centered
			>
				<Modal.Header closeButton={!isRunning}>
					<Modal.Title>{t('motornet_import_save', 'Salva')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="mb-3">
						{t('motornet_import_confirm', {
							count: importableItems.length,
							defaultValue_one: 'Importa {{count}} veicolo valido',
							defaultValue_other: 'Importa {{count}} veicoli validi',
						})}{' '}
						{t('motornet_import_confirm_skipped', {
							count: enrichedRows.length - importableItems.length,
							defaultValue_one: '({{count}} ignorato).',
							defaultValue_other: '({{count}} ignorati).',
						})}
					</p>

					{(isRunning || progress.done > 0) && (
						<>
							<ProgressBar className="mb-2">
								<ProgressBar
									variant="success"
									now={progress.success}
									max={progress.total}
									key="success"
								/>
								<ProgressBar
									variant="danger"
									now={progress.error}
									max={progress.total}
									key="error"
								/>
							</ProgressBar>
							<div className="text-muted small">
								{progress.done}/{progress.total}
								{progress.error > 0 &&
									` · ${progress.error} ${t(
										'motornet_import_failed',
										'falliti'
									)}`}
							</div>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					{isRunning ? (
						<Btn size="sm" disabled>
							<Spinner animation="border" size="sm" className="me-2" />
							{t('motornet_import_running', 'Importazione in corso…')}
						</Btn>
					) : (
						<>
							{progress.done === 0 && (
								<Btn size="sm" onClick={handleStartImport}>
									{t('motornet_import_start', 'Avvia')}
								</Btn>
							)}
							{progress.error > 0 && (
								<Btn size="sm" onClick={handleRetryFailed}>
									{t('motornet_import_retry_failed', 'Riprova falliti')} (
									{progress.error})
								</Btn>
							)}
							{progress.done > 0 && (
								<Btn size="sm" onClick={() => setSaveModalOpen(false)}>
									{t('close', 'Chiudi')}
								</Btn>
							)}
						</>
					)}
				</Modal.Footer>
			</Modal>
		</Container>
	);
};

export default MotornetImportPage;
